import React, { Fragment, ReactElement } from 'react';
import { Dropdown } from 'react-bootstrap';
import { blankLink } from 'config';
import { useTranslation } from 'react-i18next';
import { AppLink } from 'App/components/utils/routing/AppLink';
import { useAppAccounts, useAppUser, appUserPathPrefix } from 'App/components/utils/providers/AppUserProvider';
import { permissionDict as p } from 'Services/permissions/permission-dict';
import { useCurrentProfileAvatar } from 'App/components/utils/hooks';
import { useAppSecurity } from 'App/components/utils/providers/AppProductProvider';
import { IAppUserMeta } from '../../../types';
import { NavWorkspaces, Workspace } from './NavWorkspaces';
import { useAppLogger } from 'Services/logger';
import { useWorkspaceHook } from './hooks/useWorkspaceHook';
import { useWorkspacesApi } from 'Services/hooks/ApiHook';
import { publicApiUrl } from 'App/utils';
import { listWorkspaces } from './listWorkspaces';
import { useAppPageContext } from 'App/components/utils/pages/context';
import { AppLayoutAction } from '../reducers/app-layout-reducer';

export interface UserItem {
  user_id?: number;
  email: string;
  avatar: string;
  company_name: string;
  themeColor?: string;
}

export interface CurrentWorkspace {
  workspaceName: string;
  tenantName: string;
}

const emilLogoSrc = '/assets/logos/emil-demo.png';
const emilLogoLargeSrc = '/assets/logos/emil-demo-large.png';

export const NavProfile: React.FC = () => {
  const { t } = useTranslation( [ 'base' ] );
  const idPrefix: string = 'nav-profile-widget';
  const user = useAppSecurity();
  const currentAccount = useAppUser();
  const accounts = useAppAccounts();
  const userAvatar = useCurrentProfileAvatar();
  const workspaceService = useWorkspacesApi();
  const logger = useAppLogger();
  const utils = useWorkspaceHook();
  const isFirstRender = React.useRef( true );
  const [ workspaces, setWorkspaces ] = React.useState<Workspace[]>( [] );
  const [ currentWorkspace, setCurrentWorkspace ] = React.useState<CurrentWorkspace>(
    { workspaceName: '', tenantName: '' } );
  const layoutCtx = useAppPageContext<AppLayoutAction>();
  const canEditOrAddAccount = user.hasPermissions( [ p.as_GetAccount ] );

  React.useEffect( () => {
    const loadWorkspaces = async (): Promise<void> => {
      try {
        const list = ( await listWorkspaces(
          currentAccount.authMode,
          accounts.length,
          workspaceService,
          publicApiUrl || '',
          ( error ) => logger.warn( 'Workspaces: the cookie call failed, used the SDK instead', error ),
          currentAccount.sub,
        ) ) as Workspace[];
        const current = list.find( ( workspace ) => workspace.slug === currentAccount.tenantSlug );
        if ( current ) {
          const tenantName = utils.isMultiTenantList( list ) ? current.tenantName || '' : '';
          setCurrentWorkspace( {
            tenantName,
            workspaceName: utils.getWorkspaceName( current ),
          } );
          layoutCtx.fireAction( { type: 'UPDATE_WORKSPACE_NAME', payload: { workspaceName: current.name } } );
        }
        setWorkspaces( list );
      } catch ( error ) {
        logger.error( error );
      }
    };

    if ( isFirstRender ) {
      loadWorkspaces();
    }
  }, [
    accounts.length, currentAccount.authMode, currentAccount.sub, currentAccount.tenantSlug, layoutCtx, logger, utils,
    workspaceService,
  ] );

  const identifyUserToAnalytics = React.useCallback( ( userData: IAppUserMeta ): void => {
    analytics?.reset();
    analytics?.identify(
      userData.sub,
      { tenantId: userData.tenantId },
    );
  }, [] );

  const otherAccounts: ReactElement[] = [];
  for ( let i = 0; i < accounts.length; i++ ) {
    if ( i !== currentAccount.accountIndex ) {
      const account = accounts[i];
      const accountLink: ReactElement = (
        <li className="other-profile" key={ `profille-${ i }` }>
          <AppLink
            key={ i }
            to={ appUserPathPrefix( i ) }
            onClick={ () => identifyUserToAnalytics( account ) }
            withoutUserContext
            className="dropdown-item align-items-center"
            id={ `switchProfile-${ i }` }
          >
            <span className="other-profile-avatar float-left m-l-5 m-r-10">
              <img
                src={ emilLogoSrc }
                className="img-radius user-avatar small-avatar-with-border border-0 m-auto d-block"
                alt="Emil Logo"
              />
            </span>
            <span className="other-profile-details float-left">
              <h5 className="f-14 mb-0">{ account.tenantName }</h5>
              <span className="f-12">{ account.userName }</span>
            </span>
          </AppLink>
        </li>
      );
      otherAccounts.push( accountLink );
    }
  }

  React.useEffect( () => {
    isFirstRender.current = false;
  }, [] );

  return (
    <Fragment>
      <Dropdown.Toggle variant={ 'link' } id="dropdownProfile" className="p-0 m-0">
        <img src={ userAvatar }
          className="img-radius user-avatar small-avatar" alt="User Profile"
        />
      </Dropdown.Toggle>
      <Dropdown.Menu alignRight className="profile-notification" id={ `${idPrefix}-userProfileMenu` }>
        <div className="text-center pb-3 pt-3">
          <img
            src={ emilLogoLargeSrc }
            className="img-fluid rounded-circle user-avatar tenant-logo"
            alt="Emil Logo"
          />
        </div>
        <div className="text-center profile-details pl-2 pr-2">
          { currentAccount.userFullName && (
            <h5 id={ `${idPrefix}-userFullName` } className="mt-3 f-16 mb-0">{ currentAccount.userFullName }</h5>
          ) }
          { currentAccount.userName && (
            <span id={ `${idPrefix}-userName` } className="d-block f-14 mt-1">{ currentAccount.userName }</span>
          ) }
          { workspaces.length > 1 && currentWorkspace.workspaceName && (
            <>
              <span id={ `${idPrefix}-workspace` } className="d-block f-14 mt-1">
                { `${t( 'profile.workspace' )}: ${currentWorkspace.workspaceName}` }
              </span>
              { currentWorkspace.tenantName && (
                <span className='d-block f-12 mt-0'>({ currentWorkspace.tenantName })</span>
              ) }
            </>
          ) }
        </div>
        <ul className="pro-body">
          { canEditOrAddAccount &&
            <Fragment>
              <li>
                <Dropdown.Item
                  id={ `${idPrefix}-settingPage` }
                  href={ blankLink }
                  role="button"
                >
                  <i className="feather icon-settings" /> { t( 'profile.settings' ) }
                </Dropdown.Item>
              </li>
            </Fragment>
          }

          { workspaces.length > 1 && <NavWorkspaces workspaces={ workspaces } /> }
          { otherAccounts.length || ( workspaces.length > 1 && canEditOrAddAccount )
            ? <hr className="mt-3 mb-3" /> : null }
          { otherAccounts }
          { canEditOrAddAccount &&
            <li className="mt-1">
              <AppLink
                id={ `${idPrefix}-addAnotherAccount` }
                to="/login"
                withoutUserContext
                className="dropdown-item"
              >
                <i className="feather icon-user-plus" /> { t( 'profile.addAnotherAccount' ) }
              </AppLink>
            </li>
          }
        </ul>
        <hr className="mt-0 mb-3"></hr>
        <div className="text-center mb-3">
          <AppLink
            id={ `${idPrefix}-logout` }
            to="/logout"
            className="logout-btn m-r-10 m-b-5 btn btn-outline-secondary"
          >
            { t( 'profile.logout' ) }
          </AppLink>
        </div>
      </Dropdown.Menu>
    </Fragment>
  );
};
